import React, { FC, useState } from 'react';
import { Formik } from 'formik';
import { useMutation } from '@apollo/react-hooks';
import { PartnerType } from 'Admin/Partners';
import { UserFormValues, UserForm, UserLicenses } from 'Admin/Users/components';
import { UserType } from 'Admin/Users/types';
import {
  getGraphqlValidationErrors,
  hasGraphqlValidationFailures,
  getGraphqlErrors,
} from 'Admin/utils';
import { useToast, HRule } from 'Shared/components';
import { UserActivation } from '../UserActivation';
import { footerErrorMessage } from '../../CreateUserDetails';
import {
  UPDATE_USER,
  GET_USER_BY_ID,
  ADD_ROLE,
  DELETE_ROLE,
  UPDATE_ACTIVATION,
  GET_ROLES_BY_PARTNER_QUERY,
} from 'Admin/Users/graphql';
import { userValidationSchema } from '../validation';
import { MoveUser } from '../MoveUser/MoveUser';

type UserActivationUpdateInput = { portalUserId: string; activated: boolean };

export const UpdateUserForm: FC<{
  user: UserType;
  currentPartner?: PartnerType;
}> = ({ user, currentPartner }) => {
  const { toast } = useToast();

  const [updatePortalUser, { error: updateError }] = useMutation<
    {},
    {
      id: string;
      firstName: string;
      lastName: string;
      suffix: string;
      nationalProducerNumber: string;
      phoneNumber: string;
      lineOne: string;
      lineTwo: string;
      city: string;
      zip: string;
      state: string;
      email: string;
    }
  >(UPDATE_USER, {
    refetchQueries: [
      {
        query: GET_USER_BY_ID,
        variables: { id: user.id },
      },
      {
        query: GET_ROLES_BY_PARTNER_QUERY,
        variables: { partnerId: currentPartner?.id },
      },
    ],
  });

  const [hasActivationError, setHasActivationError] = useState(false);

  const [addRole, { error: addRoleError }] = useMutation<
    {},
    {
      portalUserId: string;
      roleName: string;
      scope: string;
      scopeId: string;
    }
  >(ADD_ROLE);

  const [removeRole, { error: removeRoleError }] = useMutation<
    {},
    {
      id: string;
    }
  >(DELETE_ROLE);

  // Apollo Mutation for setting the user's active status
  const [portalUserActivationUpdate, { error: activationError }] = useMutation<
    {},
    UserActivationUpdateInput
  >(UPDATE_ACTIVATION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GET_USER_BY_ID,
        variables: { id: user.id },
      },
    ],
  });

  const isUserWithPartner = !!currentPartner;

  const { lineOne = '', lineTwo = '', city = '', state = '', zipCode = '' } =
    user.address || {};

  const roles = user.portalUserGrants
    .filter(grant =>
      isUserWithPartner
        ? grant.scope === 'partner' && grant.scopeId === currentPartner?.id
        : grant.scope === 'system'
    )
    .map(grant => grant.role.name);

  const showLicenses =
    user.licensedAgent?.nationalProducerNumber && user.licenses.length > 0;

  return (
    <Formik<UserFormValues>
      initialValues={{
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        suffix: user.suffix || '',
        originalNationalProducerNumber:
          user.licensedAgent?.nationalProducerNumber || '',
        nationalProducerNumber:
          user.licensedAgent?.nationalProducerNumber || '',
        npnStatus: user.npnStatus || '',
        phoneNumber: user.phoneNumber || '',
        address: {
          lineOne: lineOne || '',
          lineTwo: lineTwo || '',
          city: city || '',
          zipCode: zipCode || '',
          state: state || '',
        },
        roles,
        serverValidationErrors: {},
      }}
      validateOnBlur={false}
      validationSchema={userValidationSchema}
      validateOnMount
      enableReinitialize
      onSubmit={async (values, { setSubmitting, resetForm, setFieldValue }) => {
        const rolesToAdd = values.roles.filter(role => !roles.includes(role));
        const rolesToRemove = roles.filter(
          role => !values.roles.includes(role)
        );
        await Promise.all([
          updatePortalUser({
            variables: {
              id: user.id,
              firstName: values.firstName,
              lastName: values.lastName,
              suffix: values.suffix,
              nationalProducerNumber: values.nationalProducerNumber,
              phoneNumber: values.phoneNumber,
              lineOne: values.address.lineOne,
              lineTwo: values.address.lineTwo,
              city: values.address.city,
              zip: values.address.zipCode,
              state: values.address.state,
              email: values.email,
            },
          }),
          ...rolesToAdd.map(async roleToAdd => {
            return addRole({
              variables: {
                portalUserId: user.id,
                roleName: roleToAdd,
                scope: isUserWithPartner ? 'partner' : 'system',
                scopeId: currentPartner?.id || '',
              },
            });
          }),
          ...rolesToRemove.map(async roleToRemove => {
            const grantToRemove = user.portalUserGrants.find(grant => {
              const scope = isUserWithPartner ? 'partner' : 'system';
              return grant.scope === scope && grant.role.name === roleToRemove;
            });
            return (
              grantToRemove &&
              removeRole({
                variables: {
                  id: grantToRemove.id,
                },
              })
            );
          }),
        ])
          .then(() => {
            toast({
              message: 'Page has been saved.',
              type: 'success',
              position: 'bottom',
              withSecondaryNav: true,
            });
            resetForm({ values });
          })
          .catch(err => {
            // Other situations are handled via api hook errors
            const graphqlValidationErrors = getGraphqlValidationErrors(err);
            if (graphqlValidationErrors) {
              setFieldValue('serverValidationErrors', graphqlValidationErrors);
            }
          })
          .finally(() => {
            setSubmitting(false);
          });
      }}
    >
      {({ values, setFieldValue, resetForm, validateField }) => {
        const hasNpnError = !!values.serverValidationErrors
          .nationalProducerNumber;
        const disabled = !user.activated;

        const getFooterError = () => {
          if (hasNpnError) return footerErrorMessage;
          const apiError =
            updateError || addRoleError || removeRoleError || activationError;
          if (apiError && !hasGraphqlValidationFailures(apiError)) {
            // Display the api error if it is not a validation_failures object
            const grapqlErrors = getGraphqlErrors(apiError);
            return grapqlErrors?.message || grapqlErrors || apiError.message;
          }
          return undefined;
        };

        const handleActivationClick = (activated: boolean) => {
          return () => {
            portalUserActivationUpdate({
              variables: {
                portalUserId: user.id,
                activated: activated,
              },
            })
              .then(() => {
                // after successful activation
                // reset the field npn errors
                resetForm();
                setHasActivationError(false);
                validateField('nationalProducerNumber');
              })
              .catch((err: Error) => {
                const graphqlValidationErrors = getGraphqlValidationErrors(err);
                if (graphqlValidationErrors) {
                  setHasActivationError(true);
                  setFieldValue(
                    'serverValidationErrors',
                    graphqlValidationErrors
                  );
                }
              });
          };
        };

        const allowNavigatingFromUserDetails = hasActivationError; // it's okay to navigate away if the user encounters an activation error.

        return (
          <>
            <UserForm
              type="edit"
              disabled={disabled}
              hasValidationError={hasNpnError}
              currentUser={user}
              currentPartner={currentPartner}
              footerError={getFooterError()}
              allowNavigatingAway={allowNavigatingFromUserDetails}
            />
            <HRule size="lg" />
            {showLicenses && (
              <UserLicenses
                licenses={user.licenses}
                partnerId={currentPartner?.id}
              />
            )}
            <MoveUser user={user} currentPartner={currentPartner} />
            <UserActivation user={user} handleClick={handleActivationClick} />
          </>
        );
      }}
    </Formik>
  );
};
